import Clickable from 'components/Common/Clickable'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { getTotalRoomsString, prettyDates } from 'components/Search/utils'
import config from 'constants/config'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { navBrandLogoProductLabel } from 'i18n/productLabels'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import Heading from 'LuxKit/Typography/Heading'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import MetaSearchV2SearchBarDescription from './MetaSearchV2SearchBarDescription'

const Container = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const Bar = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: ${rem(12)};
`

const BrandLink = styled(ClickableLink)`
  width: ${rem(32)};
  height: ${rem(32)};
`

const EditTrigger = styled(Clickable)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const TextContainer = styled.div`
  min-width: 0;
`

const EditIcon = styled(LinePenIcon)`
  flex-shrink: 0;
`

interface Props {
  onEdit: () => void
}

function MetaSearchV2SearchBar({ onEdit }: Props) {
  const intl = useIntl()
  const {
    checkinDate,
    checkoutDate,
    occupancies,
    searchTargetLabel,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const anytimeText = intl.formatMessage({
    id: 'MTyXbCi6rfUaKnk0bVLP',
    defaultMessage: 'Anytime',
    description: 'Label shown when no specific date is selected for the metasearch mobile search header',
  })

  const mapAreaText = intl.formatMessage({
    id: 'P-S_FqWaQCC-u2sLp_r4',
    defaultMessage: 'Map area',
    description: 'Fallback location label in the metasearch mobile search header when no search target is set',
  })

  const editSearchLabel = intl.formatMessage({
    id: 'puU-OeK3AyL1ZgtAIxVJ',
    defaultMessage: 'Edit search',
    description: 'Accessibility label for the edit button on the metasearch mobile search header',
  })

  const searchBarDescription = useMemo((): ComponentProps<typeof MetaSearchV2SearchBarDescription> => {
    const occupancyWithRoomDetails = occupancies ?
      `${formatOccupantsAsGuests(...occupancies)} ${getTotalRoomsString(intl, occupancies)}` :
      undefined

    if (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME) {
      return {
        primaryText: anytimeText,
        secondaryText: occupancyWithRoomDetails,
      }
    }

    const datesDetails = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ?
        prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, undefined, intl) :
        prettyDates(intl, checkinDate, checkoutDate)

    return {
      primaryText: datesDetails,
      secondaryText: occupancyWithRoomDetails,
    }
  }, [dateSearchOptionId, userSelectedFlexibleMonths, flexibleMonths, flexibleNights, checkinDate, checkoutDate, occupancies, anytimeText, intl])

  return (
    <Container>
      <Bar>
        <BrandLink to={config.header.logo.to} aria-label={intl.formatMessage(navBrandLogoProductLabel[config.BRAND])}>
          <LogoIcon
            mobileVariant="icon"
            tabletVariant="icon"
            desktopVariant="icon"
            extraLargeDesktopVariant="icon"
            height="100%"
            width={null}
          />
        </BrandLink>
        <EditTrigger onClick={onEdit} aria-label={editSearchLabel}>
          <TextContainer>
            <Heading variant="heading6" lineClamp={1}>{searchTargetLabel ?? mapAreaText}</Heading>
            <MetaSearchV2SearchBarDescription {...searchBarDescription} />
          </TextContainer>
          <EditIcon size={24} />
        </EditTrigger>
      </Bar>
    </Container>
  )
}

export default MetaSearchV2SearchBar
